import { SerializerWrapper } from "@anna-money/anna-web-lib/api";
import { type Property, Serializer, custom } from "@anna-money/anna-web-lib/serializer";
import { type CustomSerializer, SerializerOld } from "@anna-money/anna-web-lib/serializerOld";
import { AnnaError, DateOnly, Decimal, convertCase, dateOnlyFormats } from "@anna-money/anna-web-lib/utils";
import { Datetime } from "types/date";

export const convertCamelCaseToSnake = convertCase.bind(null, "camel", "snake");
export const convertSnakeCaseToCamel = convertCase.bind(null, "snake", "camel");

export const DateOnlySerializer: CustomSerializer<string, DateOnly> = {
  type: DateOnly,
  serialize: (x) => x.format(dateOnlyFormats.ISO),
  deserialize: (x) => {
    if (typeof x !== "string") {
      throw new Error(`Expected date field to be a string but ${x as string} received`);
    }
    return DateOnly.parse(x);
  },
};

export const DecimalSerializer: CustomSerializer<string, Decimal> = {
  type: Decimal,
  serialize: (x) => x.toString(),
  deserialize: (x) => {
    if (typeof x !== "string") {
      throw new Error(`Expected decimal field to be a string but ${x as string} received`);
    }
    return Decimal.parse(x);
  },
};

export const dateAndTime = (): Property<Date> =>
  custom(
    "Date",
    (x: unknown) => {
      if (typeof x !== "string" && typeof x !== "number") {
        throw new AnnaError(`Date expected a string or number to parse but ${typeof x} received`, {
          extra: {
            value: x,
          },
        });
      }
      const date = new Date(x);
      if (isNaN(date.getTime())) {
        throw new AnnaError(`Date expected a valid date string or timestamp but invalid value received`, {
          extra: {
            value: x,
          },
        });
      }
      return date;
    },
    (x: Date) => x.toISOString(),
  );

export const datetime = (): Property<Datetime> =>
  custom(
    "Datetime",
    (x: unknown) => {
      if (typeof x !== "string" && typeof x !== "number") {
        throw new AnnaError(`Datetime expected a string or number to parse but ${typeof x} received`, {
          extra: {
            value: x,
          },
        });
      }
      return Datetime.parse(typeof x === "number" ? new Date(x).toISOString() : x);
    },
    (x: Datetime) => x.toISOString(),
  );

export const dateOnly = (): Property<DateOnly> =>
  custom(
    "DateOnly",
    (x: unknown) => {
      if (typeof x !== "string") {
        throw new AnnaError(`DateOnly expected a string to parse but ${typeof x} received`, {
          extra: {
            value: x,
          },
        });
      }
      return DateOnly.parse(x);
    },
    (x: DateOnly) => x.format(dateOnlyFormats.ISO),
  );

export const genericObject = (): Property<Record<string, any>> =>
  custom(
    "Record<string, any>",
    (x: unknown) => {
      // Handle null/undefined by returning empty object
      if (x === null || x === undefined) {
        return {};
      }

      if (typeof x !== "object" || Array.isArray(x)) {
        throw new AnnaError(`Generic object expected an object but ${typeof x} received`, {
          extra: {
            value: x,
          },
        });
      }

      // Convert snake_case keys to camelCase for deserialization
      const result: Record<string, any> = {};
      for (const [key, value] of Object.entries(x as Record<string, any>)) {
        const camelKey = convertCase("snake", "camel", key);
        result[camelKey] = value;
      }
      return result;
    },
    (x: Record<string, any>) => {
      // Handle null/undefined during serialization
      if (x === null || x === undefined) {
        return null;
      }

      // Convert camelCase keys to snake_case for serialization
      const result: Record<string, any> = {};
      for (const [key, value] of Object.entries(x)) {
        const snakeKey = convertCase("camel", "snake", key);
        result[snakeKey] = value;
      }
      return result;
    },
  );

export const DatetimeSerializer: CustomSerializer<string, Datetime> = {
  type: Datetime,
  serialize: (x) => x.toISOString(),
  deserialize: (x) => {
    if (typeof x !== "string") {
      throw new Error(`Expected date field to be a string but ${x as string} received`);
    }
    return Datetime.parse(x);
  },
};

export const serializer = new SerializerOld({
  formatPropertyNames: convertCase.bind(null, "camel", "snake"),
  serializers: [DecimalSerializer, DateOnlySerializer],
  propertiesRequired: true,
});

export const serializerNew = new Serializer((x: string) => convertCase("camel", "snake", x));

export const serializerWrapper = new SerializerWrapper(serializer, serializerNew);

export const apiSerializer = new SerializerOld({
  formatPropertyNames: convertCamelCaseToSnake,
  propertiesRequired: true,
  serializers: [DatetimeSerializer],
});

export const apiSerializerNew = new Serializer(convertCamelCaseToSnake);
